<template>
  <apexchart height="100%" :options="chartOptions" :series="chartSeries"></apexchart>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { formatCurrencyUSD, computeWeightedNetPnl } from '@/utils/funcs.js';

const DELTA_QTY_EPS = 1e-8;

export default {
  name: 'EvolBreakEvenChart',
  props: {
    timeSeries: { type: Array, required: true },
    aggLabels:  { type: Array, required: true },
  },
  computed: {
    parsedTimeSeries() {
      return this.timeSeries.map(({ index, m }) => {
        const snapshot = m?.manualsSnapshot || {};
        const currentPrice = m?.currentPrice || 0;

        const spotShorts = (snapshot.spotShorts || []).map(s => ({ price: Number(s.p), qty: Number(s.q) }));
        const futuresShorts = (snapshot.futuresShorts || []).map(s => ({ price: Number(s.p), qty: Number(s.q) }));
        const shortsList = [...spotShorts, ...futuresShorts];

        const spotLongs = (snapshot.spotLongs || []).map(l => ({ price: Number(l.p), qty: Number(l.q) }));
        const futuresLongs = (snapshot.futuresLongs || []).map(l => ({ price: Number(l.p), qty: Number(l.q) }));
        const longsList = [...spotLongs, ...futuresLongs];

        const shortsTotalQty = shortsList.reduce((acc, s) => acc + s.qty, 0);
        const shortsNotional = shortsList.reduce((acc, s) => acc + (s.qty * s.price), 0);
        const longsTotalQty = longsList.reduce((acc, l) => acc + l.qty, 0);
        const longsNotional = longsList.reduce((acc, l) => acc + (l.qty * l.price), 0);

        const marked = computeWeightedNetPnl({
          longsQty: longsTotalQty,
          longsNotional,
          shortsQty: shortsTotalQty,
          shortsNotional,
          markPrice: currentPrice,
        });
        const shortsAvgPrice = marked.shortsAvg;
        const longsAvgPrice = marked.longsAvg;
        const totalShortPnL = marked.totalShortPnL;
        const totalLongPnL = marked.totalLongPnL;
        const totalNetPnL = marked.totalNetPnl;

        const deltaQty = longsTotalQty - shortsTotalQty;
        const hasPositions = longsTotalQty > 0 || shortsTotalQty > 0;
        const breakEven = hasPositions && Math.abs(deltaQty) > DELTA_QTY_EPS
          ? (longsNotional - shortsNotional) / deltaQty
          : null;
        const pmpSpread = (shortsTotalQty > 0 && longsTotalQty > 0)
          ? shortsAvgPrice - longsAvgPrice
          : null;

        return {
          index,
          currentPrice,
          shortsAvgPrice,
          longsAvgPrice,
          shortsTotalQty,
          longsTotalQty,
          deltaQty,
          totalShortPnL,
          totalLongPnL,
          totalNetPnL,
          breakEven,
          pmpSpread,
        };
      });
    },

    chartSeries() {
      const breakEvenArray = [];
      const netPnLArray = [];
      const pmpSpreadArray = [];

      this.parsedTimeSeries.forEach((item) => {
        const i = item.index;
        breakEvenArray.push([i, item.breakEven != null ? Number(item.breakEven.toFixed(2)) : null]);
        netPnLArray.push([i, Number(item.totalNetPnL.toFixed(2))]);
        pmpSpreadArray.push([i, item.pmpSpread != null ? Number(item.pmpSpread.toFixed(2)) : null]);
      });

      return [
        { name: 'Break Even', type: 'line', data: breakEvenArray },
        //{ name: 'Net PnL',    type: 'line', data: netPnLArray },
        { name: 'Δ PMP',      type: 'line', data: pmpSpreadArray },
      ];
    },

    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        colors: ['#f59e0b', '#3b82f6', '#a78bfa'],
        grid: {
          borderColor: '#333',
          padding: { right: 30, left: 15 },
        },
        xaxis: { type: 'numeric', labels: { formatter: this.xAxisFormatter } },
        yaxis: [
          {
            seriesName: 'Break Even',
            title: { text: 'Break Even (USD)' },
            labels: { show: true, formatter: formatCurrencyUSD },
          },
          {
            seriesName: 'Net PnL',
            opposite: true,
            title: { text: 'Net PnL / Δ PMP (USD)' },
            labels: { show: true, formatter: formatCurrencyUSD },
          },
          {
            seriesName: 'Net PnL',
            show: false,
          },
        ],
        tooltip: {
          shared: false,
          intersect: false,
          custom: function({ dataPointIndex }) {
            return self.generateTooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    xAxisFormatter(val) {
      return this.aggLabels[Math.floor(val)] || '';
    },
    generateTooltipHtml(dataPointIndex) {
      const item = this.parsedTimeSeries[dataPointIndex];
      if (!item) return '';

      const label = this.aggLabels[dataPointIndex] || '';
      const fmtPrice = (v) => {
        if (v == null) return '—';
        return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };
      const fmtCurrency = (v) => {
        if (v == null) return '$0.00';
        const formatted = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return v < 0 ? `-$${formatted}` : `$${formatted}`;
      };

      const netPnL = item.totalNetPnL || 0;
      const netPnLColor = netPnL > 0 ? '#26a69a' : netPnL < 0 ? '#ef5350' : '#e3b341';
      const deltaQty = item.deltaQty || 0;
      const deltaQtyColor = deltaQty > 0 ? '#26a69a' : deltaQty < 0 ? '#ef5350' : '#e3b341';
      const vsMarket = item.breakEven != null && item.currentPrice
        ? item.currentPrice - item.breakEven
        : null;
      const vsMarketColor = vsMarket > 0 ? '#26a69a' : vsMarket < 0 ? '#ef5350' : '#e3b341';
      const pmpSpreadColor = item.pmpSpread > 0 ? '#26a69a' : item.pmpSpread < 0 ? '#ef5350' : '#a78bfa';

      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 280px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #38bdf8;">
            📅 ${label}
          </div>
          <div style="line-height: 1.7;">
            <div>💰 Preço BTC: <b>${fmtPrice(item.currentPrice)}</b></div>
            <div style="color: #f59e0b;">⚖️ Break Even: <b>${fmtPrice(item.breakEven)}</b></div>
            <div style="color: ${vsMarketColor};">Δ Mercado vs BE: <b>${vsMarket == null ? '—' : fmtCurrency(vsMarket)}</b></div>
            <div style="color: ${netPnLColor};">Δ Net PnL: <b>${fmtCurrency(netPnL)}</b></div>
            <div style="color: #a78bfa;">Δ PMP (Shorts − Longs): <b style="color: ${pmpSpreadColor};">${item.pmpSpread == null ? '—' : fmtCurrency(item.pmpSpread)}</b></div>
            <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #444;">
              <span style="color: #ef5350;">📉 PMP Shorts: <b>${fmtPrice(item.shortsAvgPrice)}</b> (${(item.shortsTotalQty || 0).toFixed(4)})</span><br/>
              <span style="color: #26a69a;">📈 PMP Longs: <b>${fmtPrice(item.longsAvgPrice)}</b> (${(item.longsTotalQty || 0).toFixed(4)})</span><br/>
              <span style="color: ${deltaQtyColor};">Δ Delta Qty: <b>${deltaQty.toFixed(4)}</b></span>
            </div>
          </div>
        </div>
      `;
    },
  },
};
</script>
